// frontend/src/pages/InvestmentMarketplace.jsx
import { useState, useEffect } from 'react';
import { investmentAPI } from '../services/api';
import { Card, Button, Badge, ProgressBar, StatCard, Modal, Input, Select, Spinner, Empty } from '../components/ui';
import toast from 'react-hot-toast';
import { TrendingUp, Info, ChevronRight, DollarSign, Calendar, Shield, Users } from 'lucide-react';
import { clsx } from 'clsx';

const FUND_META = {
  internet_expansion: { icon: '📡', color: '#00A8DB', riskColor: 'green' },
  spot_trading:       { icon: '₿',  color: '#3B82F6', riskColor: 'amber' },
  futures_trading:    { icon: '⚡', color: '#8B5CF6', riskColor: 'red'   },
  p2p_arbitrage:      { icon: '⇄',  color: '#14B8A6', riskColor: 'amber' },
  real_estate:        { icon: '🏢', color: '#F43F5E', riskColor: 'green' },
};

export default function InvestmentMarketplace() {
  const [funds, setFunds] = useState([]);
  const [myInvestments, setMyInvestments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('marketplace');
  const [selectedFund, setSelectedFund] = useState(null);
  const [investModal, setInvestModal] = useState(null);
  const [amount, setAmount] = useState('');
  const [investing, setInvesting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fundsRes, myRes] = await Promise.all([
        investmentAPI.getFunds(),
        investmentAPI.getMyInvestments(),
      ]);
      setFunds(fundsRes.funds || []);
      setMyInvestments(myRes.investments || []);
      setSummary(myRes.summary || null);
    } catch (err) {
      toast.error('Failed to load investment data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvest = async () => {
    if (!investModal || !amount || parseFloat(amount) < investModal.minimumInvestment) {
      toast.error(`Minimum investment: $${investModal?.minimumInvestment}`);
      return;
    }
    setInvesting(true);
    try {
      await investmentAPI.invest({ fundId: investModal._id, amount: parseFloat(amount) });
      toast.success(`Successfully invested $${amount} in ${investModal.name}!`);
      setInvestModal(null);
      setAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Investment failed');
    } finally {
      setInvesting(false);
    }
  };

  const handleWithdrawReturns = async (investmentId, fundName) => {
    try {
      const res = await investmentAPI.withdrawReturns(investmentId);
      toast.success(res.message || 'Returns credited to wallet');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Withdrawal failed');
    }
  };

  // Return estimator for invest modal
  const estMonthly = amount && investModal
    ? {
        lo: (parseFloat(amount) * investModal.minMonthlyReturn / 100).toFixed(2),
        hi: (parseFloat(amount) * investModal.maxMonthlyReturn / 100).toFixed(2),
      }
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-text-primary tracking-tight">Investment Marketplace</h1>
        <p className="text-text-secondary text-sm mt-1">Choose where to invest your capital. Returns credited monthly to your wallet.</p>
      </div>

      {/* User Journey Banner */}
      <div className="flex items-center gap-2 bg-bg-panel border border-border rounded-2xl px-5 py-3 overflow-x-auto">
        {['Deposit', '→', 'Choose Fund', '→', 'Invest', '→', 'Monthly Returns', '→', 'Withdraw or Reinvest'].map((s, i) => (
          <span key={i} className={clsx(
            'text-xs whitespace-nowrap',
            i % 2 === 0 ? 'text-cyan font-bold' : 'text-text-muted'
          )}>{s}</span>
        ))}
      </div>

      {/* Portfolio stats */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Invested"    value={`$${summary.totalInvested?.toFixed(2) || '0.00'}`} color="#8B5CF6" icon="🏗️"/>
          <StatCard label="Returns Earned"    value={`$${summary.totalReturns?.toFixed(2) || '0.00'}`}  color="#10B981" icon="💸"/>
          <StatCard label="Active Funds"      value={summary.activeCount || 0}                           color="#00A8DB" icon="📊"/>
          <StatCard label="Total Withdrawn"   value={`$${summary.totalWithdrawn?.toFixed(2) || '0.00'}`} color="#EAB308" icon="📅"/>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {[['marketplace', 'Investment Marketplace'], ['my', 'My Investments'], ['journey', 'How It Works']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id
                ? 'bg-cyan text-bg'
                : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}
          >{label}</button>
        ))}
      </div>

      {/* MARKETPLACE */}
      {tab === 'marketplace' && (
        <div className="grid md:grid-cols-2 gap-4">
          {funds.map((fund) => {
            const meta = FUND_META[fund.slug] || { icon: '📊', color: '#00A8DB', riskColor: 'amber' };
            const isSelected = selectedFund === fund._id;
            return (
              <Card
                key={fund._id}
                className={clsx('cursor-pointer transition-all duration-200', isSelected && 'ring-1')}
                style={{ borderColor: isSelected ? meta.color : undefined, ...(isSelected ? { boxShadow: `0 0 20px ${meta.color}20` } : {}) }}
                onClick={() => setSelectedFund(isSelected ? null : fund._id)}
              >
                {/* Top accent */}
                <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl" style={{ background: meta.color }} />

                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                      style={{ background: `${meta.color}20`, border: `2px solid ${meta.color}40` }}>
                      {meta.icon}
                    </div>
                    <div>
                      <p className="text-text-primary font-bold text-sm leading-tight">{fund.name}</p>
                      <Badge label={`Risk: ${fund.riskLevel}`} color={meta.riskColor} />
                    </div>
                  </div>
                  <ChevronRight className={clsx('w-4 h-4 text-text-muted transition-transform', isSelected && 'rotate-90')} style={{ color: isSelected ? meta.color : undefined }} />
                </div>

                {/* Return highlight */}
                <div className="rounded-xl p-3 mb-4" style={{ background: `${meta.color}12`, border: `1px solid ${meta.color}25` }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-1">Expected Monthly Return</p>
                      <p className="font-black text-2xl leading-none tracking-tight" style={{ color: meta.color }}>
                        {fund.minMonthlyReturn}% – {fund.maxMonthlyReturn}%
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-1">Minimum</p>
                      <p className="text-text-primary font-black text-xl">${fund.minimumInvestment}</p>
                    </div>
                  </div>
                </div>

                {/* Progress */}
                {fund.totalTarget && (
                  <ProgressBar
                    pct={(fund.totalRaised / fund.totalTarget) * 100}
                    color={meta.color}
                    label={`$${fund.totalRaised?.toLocaleString()} raised of $${fund.totalTarget?.toLocaleString()}`}
                    value={`${Math.round((fund.totalRaised / fund.totalTarget) * 100)}%`}
                  />
                )}

                {/* Expanded detail */}
                {isSelected && (
                  <div className="border-t border-border pt-4 mt-2 space-y-3 animate-slide-up">
                    <p className="text-text-secondary text-xs leading-relaxed">{fund.description}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ['Investors', fund.totalInvestors, <Users className="w-3 h-3"/>],
                        ['Period', 'Monthly', <Calendar className="w-3 h-3"/>],
                        ['Lock Period', `${fund.lockPeriodDays} days`, <Shield className="w-3 h-3"/>],
                        ['Min Investment', `$${fund.minimumInvestment}`, <DollarSign className="w-3 h-3"/>],
                      ].map(([l, v, icon]) => (
                        <div key={l} className="bg-bg-panel rounded-lg px-3 py-2">
                          <div className="flex items-center gap-1 text-text-muted mb-1" style={{ fontSize: 10 }}>{icon}<span className="uppercase tracking-wider font-bold">{l}</span></div>
                          <p className="text-text-primary text-xs font-bold">{v}</p>
                        </div>
                      ))}
                    </div>

                    {/* Risk warning for futures */}
                    {fund.riskLevel === 'high' && (
                      <div className="flex gap-2 bg-red/10 border border-red/25 rounded-xl p-3">
                        <Info className="w-4 h-4 text-red flex-shrink-0 mt-0.5" />
                        <p className="text-red text-xs">High risk investment. Futures trading involves substantial risk of loss. Only invest what you can afford to lose.</p>
                      </div>
                    )}

                    <Button
                      className="w-full mt-2"
                      style={{ background: meta.color, borderColor: meta.color }}
                      onClick={(e) => { e.stopPropagation(); setInvestModal(fund); setAmount(String(fund.minimumInvestment)); }}
                      disabled={!fund.isOpen}
                    >
                      {fund.isOpen ? `Invest in ${fund.name} →` : 'Fund Currently Closed'}
                    </Button>
                  </div>
                )}

                {!isSelected && (
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-text-muted text-xs">{fund.totalInvestors} investors</span>
                    <span className="text-xs font-semibold" style={{ color: meta.color }}>View Details</span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* MY INVESTMENTS */}
      {tab === 'my' && (
        <div className="space-y-4">
          {myInvestments.length === 0
            ? <Empty icon="📊" title="No active investments" description="Visit the marketplace to start investing in our managed funds." action={<Button onClick={() => setTab('marketplace')}>Browse Funds</Button>} />
            : myInvestments.map((inv) => {
              const meta = FUND_META[inv.fund?.slug] || { icon: '📊', color: '#00A8DB' };
              const withdrawable = inv.totalReturnsEarned - inv.totalWithdrawn;
              return (
                <Card key={inv._id} style={{ borderColor: `${meta.color}40` }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{meta.icon}</span>
                      <div>
                        <p className="text-text-primary font-bold text-sm">{inv.fund?.name}</p>
                        <p className="text-text-muted text-xs">Since {new Date(inv.startDate).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Badge label={inv.status} color={inv.status === 'active' ? 'green' : 'gray'} />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    {[
                      ['Invested', `$${inv.principalAmount.toFixed(2)}`, '#E8EFF8'],
                      ['Returns Earned', `$${inv.totalReturnsEarned.toFixed(2)}`, '#10B981'],
                      ['Withdrawable', `$${withdrawable.toFixed(2)}`, meta.color],
                      ['Monthly Rate', `${inv.fund?.minMonthlyReturn}–${inv.fund?.maxMonthlyReturn}%`, meta.color],
                    ].map(([l, v, c]) => (
                      <div key={l} className="bg-bg-panel rounded-xl p-3">
                        <p className="text-text-muted text-[10px] uppercase tracking-wider font-bold mb-1">{l}</p>
                        <p className="font-bold text-sm" style={{ color: c }}>{v}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      disabled={withdrawable <= 0}
                      onClick={() => handleWithdrawReturns(inv._id, inv.fund?.name)}
                    >
                      Withdraw Returns ${withdrawable.toFixed(2)}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => { setInvestModal(inv.fund); setAmount(''); setTab('marketplace'); }}
                    >
                      Reinvest
                    </Button>
                  </div>
                </Card>
              );
            })
          }
        </div>
      )}

      {/* HOW IT WORKS */}
      {tab === 'journey' && (
        <Card>
          <h3 className="text-text-primary font-bold text-sm mb-4">How DWI Fintech Investments Work</h3>
          <div className="space-y-0">
            {[
              { step: '1', title: 'Deposit Funds', desc: 'Add money via MTN MoMo, Airtel Money, agent cash, or USDT. Funds appear in your wallet instantly.', color: '#00A8DB' },
              { step: '2', title: 'Choose a Fund', desc: 'Browse 5 managed funds — from low-risk ISP expansion to high-yield futures trading. Each has a clear return range and risk level.', color: '#3B82F6' },
              { step: '3', title: 'Invest', desc: 'Enter your amount (minimum varies by fund). Money moves from your wallet to the investment. You receive immediate confirmation.', color: '#8B5CF6' },
              { step: '4', title: 'Monthly Returns', desc: 'On the 1st of each month, returns are calculated and credited directly to your commission wallet — no action needed.', color: '#10B981' },
              { step: '5', title: 'Withdraw or Reinvest', desc: 'Cash out returns to MTN MoMo or Airtel Money, or reinvest to compound your growth. Principal can be withdrawn after the lock period.', color: '#EAB308' },
            ].map((s, i, arr) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm text-bg flex-shrink-0" style={{ background: s.color }}>{s.step}</div>
                  {i < arr.length - 1 && <div className="w-0.5 flex-1 my-1" style={{ background: `${s.color}40` }} />}
                </div>
                <div className="pb-6">
                  <p className="text-text-primary font-bold text-sm mb-1">{s.title}</p>
                  <p className="text-text-secondary text-xs leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* INVEST MODAL */}
      <Modal open={!!investModal} onClose={() => { setInvestModal(null); setAmount(''); }} title={`Invest in ${investModal?.name}`} size="md">
        {investModal && (() => {
          const meta = FUND_META[investModal.slug] || { icon: '📊', color: '#00A8DB' };
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: `${meta.color}12`, border: `1px solid ${meta.color}25` }}>
                <span className="text-2xl">{meta.icon}</span>
                <div>
                  <p className="text-text-primary font-bold text-sm">{investModal.name}</p>
                  <p style={{ color: meta.color }} className="text-xs font-semibold">{investModal.minMonthlyReturn}% – {investModal.maxMonthlyReturn}% monthly</p>
                </div>
              </div>

              <Input
                label="Investment Amount (USD)"
                type="number"
                placeholder={`Minimum: $${investModal.minimumInvestment}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                note={`Min $${investModal.minimumInvestment} · Returns credited monthly · ${investModal.lockPeriodDays}-day lock period`}
              />

              {/* Return estimator */}
              {estMonthly && parseFloat(amount) >= investModal.minimumInvestment && (
                <div className="bg-green/10 border border-green/25 rounded-xl p-4">
                  <p className="text-green text-xs font-bold uppercase tracking-wider mb-2">Return Estimate</p>
                  <p className="text-text-primary font-black text-lg">${estMonthly.lo} – ${estMonthly.hi} <span className="text-text-muted font-normal text-sm">per month</span></p>
                  <p className="text-text-secondary text-xs mt-1">${(parseFloat(estMonthly.lo) * 12).toFixed(0)}–${(parseFloat(estMonthly.hi) * 12).toFixed(0)} per year</p>
                </div>
              )}

              {investModal.riskLevel === 'high' && (
                <div className="bg-red/10 border border-red/25 rounded-xl p-3 flex gap-2">
                  <Info className="w-4 h-4 text-red flex-shrink-0" />
                  <p className="text-red text-xs">High risk. Futures trading involves substantial risk of loss. Only invest capital you can afford to lose.</p>
                </div>
              )}

              <Button
                className="w-full"
                style={{ background: meta.color, borderColor: meta.color }}
                loading={investing}
                disabled={!amount || parseFloat(amount) < investModal.minimumInvestment}
                onClick={handleInvest}
              >
                Activate Investment →
              </Button>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
